import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { readCreditHistory } from './creditHistoryApi'
import { formatCredits } from './billingTypes'
import { createNadDebug } from '../../utils/debugLog'

const log = createNadDebug('dw:billing-history')
const knownKinds = new Set(['welcome_bonus', 'daily_claim', 'charge', 'refund', 'topup', 'purchase', 'purchase_refund', 'operator_topup', 'operator_deduct'])

export default function CreditHistory() {
  const { t, i18n } = useTranslation('settings')
  const { user } = useAuth()
  const history = useQuery({
    queryKey: ['billing', 'credit-history', user?.id],
    queryFn: async ({ signal }) => {
      const rows = await readCreditHistory(signal)
      log({ event: 'history:loaded', trace_id: 'billing:history', row_count: rows.length })
      return rows
    },
    enabled: user !== null,
    staleTime: 30_000,
  })
  return <section className="flex flex-col gap-3" aria-labelledby="billing-history-title" data-testid="billing-history">
    <div>
      <h4 id="billing-history-title" className="text-lg font-semibold text-foreground">{t('settingsModal.billing.historyTitle')}</h4>
      <p className="mt-1 text-base text-muted-foreground">{t('settingsModal.billing.historyDescription')}</p>
    </div>
    {history.isPending ? <p role="status" className="text-base text-muted-foreground">{t('settingsModal.billing.historyLoading')}</p>
      : history.isError ? <p role="alert" className="text-base text-destructive">{t('settingsModal.billing.historyError')}</p>
      : history.data.length === 0 ? <p className="py-2 text-base text-muted-foreground" data-testid="billing-history-empty">{t('settingsModal.billing.historyEmpty')}</p>
      : <>
        <div className="overflow-x-auto rounded-xl border border-border bg-surface-0">
          <table className="w-full text-left text-base">
            <caption className="px-4 py-3 text-left font-medium text-foreground">{t('settingsModal.billing.historyCaption')}</caption>
            <thead className="border-y border-border text-muted-foreground"><tr>
              {['Date','Action','Amount'].map(column => <th key={column} scope="col" className={`whitespace-nowrap px-4 py-3 font-medium ${column === 'Amount' ? 'text-right' : ''}`}>{t(`settingsModal.billing.history${column}`)}</th>)}
            </tr></thead>
            <tbody>{history.data.map(row => <tr key={row.id} className="border-b border-border-subtle last:border-0">
              <td className="whitespace-nowrap px-4 py-3 text-muted-foreground"><time dateTime={row.createdAt}>{new Date(row.createdAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}</time></td>
              <td className="px-4 py-3 text-foreground">{knownKinds.has(row.type) ? t(`settingsModal.billing.historyKind.${row.type}`) : row.type}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-foreground">{row.credits > 0 ? '+' : ''}{formatCredits(row.credits, i18n.language)}<span className="mt-1 block font-mono text-sm text-muted-foreground">{t('settingsModal.billing.historyBalance', { credits: formatCredits(row.balanceAfter, i18n.language) })}</span></td>
            </tr>)}</tbody>
          </table>
        </div>
        <p className="text-sm text-muted-foreground">{t('settingsModal.billing.historyFootnote')}</p>
      </>}
  </section>
}
